import FuseUtils from '@/@fuse/utils/FuseUtils';
import axios from '@shared/axios-shared';
import { getSchoolProfile } from '@/app/services/settings/settings';
import secureLocalStorage from 'react-secure-storage';
import { navigate } from '@/app/navigation';

class JwtService extends FuseUtils.EventEmitter {
	init() {
		this.setInterceptors();
		this.handleAuthentication();
	}

	setInterceptors = () => {
		axios.interceptors.response.use(
			(response) => {
				return response;
			},
			(err) => {
				return new Promise(() => {
					if (err?.response?.status === 401 && err?.config && !err.config?.__isRetryRequest) {
						// if you ever get an unauthorized response, logout the user
						this.setSession(null);
						localStorage.clear();
						this.emit('onAutoLogout', err?.response?.data?.message);
						navigate('/login');
					}
					throw err;
				});
			}
		);
	};

	handleAuthentication = () => {
		const accessToken = this.getAccessToken();

		if (!accessToken) {
			this.emit('onNoAccessToken');
			return;
		}

		if (this.isAuthTokenValid(accessToken)) {
			this.setSession(accessToken);
			this.emit('onAutoLogin', true);
		} else {
			this.setSession(null);
			this.emit('onAutoLogout', 'Session expired. Please Login');
		}
	};

	createUser = (data) => {
		return new Promise((resolve, reject) => {
			axios.post('/api/auth/register', data).then((response) => {
				if (response.data.user) {
					this.setSession(response.data.access_token);
					resolve(response.data.user);
				} else {
					reject(response.data.error);
				}
			});
		});
	};

	signInWithEmailAndPassword = (username, password) => {
		return new Promise((resolve, reject) => {
			axios
				.post('/api/v2/login', {
					username,
					password,
					user_role: 'admin',
					device_name: 'web',
				})
				.then((response) => {
					if (response?.data?.access_token) {
						this.setSession(response.data.access_token);
						this.getProfile()
							.then((res) => {
								if (!res.data.verified_at) {
									// minimal user object for unverified users
									const unverifiedUser = {
										data: {
											email: res.data.email,
											verified_at: null,
											...res.data,
										},
										role: [res.data.role],
										unverified: true,
										doNotRedirect: 1,
									};
									// Set the session and mark as unverified
									this.setSession(response.data.access_token, unverifiedUser);
									try {
										const isResetPasswordPage =
											typeof window !== 'undefined' &&
											window.location &&
											typeof window.location.pathname === 'string' &&
											window.location.pathname === '/reset-password';
										if (!isResetPasswordPage) {
											navigate('/reset-password?username=' + res.data.email);
										}
									} catch (e) {
										console.error('Error setting session', e);
									}
									resolve(unverifiedUser);
								} else {
									getSchoolProfile().then((school) => {
										const user = {
											data: {
												email: res.data.email,
												displayName: `${res.data.first_name} ${res.data.last_name}`,
												photoURL: res.data.photo,
												school,
												...res.data,
											},
											permissions: response?.data?.user_permission,
											role: response?.data?.user_roles,
										};
										user.data.selected_school = school.id;
										this.setSession(response.data.access_token, user);
										resolve(user);
									});
								}
							})
							.catch((err) => {
								this.setSession(null);
								reject(err);
							});
					}
				})
				.catch((err) => {
					reject(err);
				});
		});
	};

	signInWithToken = (access_token, company_id = null, school_id = null, allow_unverified = false) => {
		let token = access_token || this.getAccessToken();
		return new Promise((resolve, reject) => {
			if (!token) {
				this.logout();
				reject(new Error('No token provided.'));
				return;
			}
			this.getProfile(token)
				.then((res) => {
					// 'sub_admin' is the fix: it was missing here, so sub_admin sessions were force-logged-out
					// on a cold token-only reload. 'super_school_admin' is a defensive add — the server never
					// returns it (it's a client-side masquerade pseudo-role) so it's inert here.
					if (
						!['super_admin', 'company_owner', 'school_admin', 'super_school_admin', 'sub_admin', 'product_owner'].includes(
							res.data.role
						)
					) {
						this.emit('onAutoLogout');
						this.setSession(null);
						reject(new Error('Cannot access this application.'));
						return;
					}

					const isResetPasswordPage =
						typeof window !== 'undefined' &&
						window.location &&
						typeof window.location.pathname === 'string' &&
						window.location.pathname.includes('/reset-password');
					const needsPasswordReset = secureLocalStorage.getItem('needs_password_reset') === 'true';

					if (!res.data.verified_at && !allow_unverified) {
						if (!isResetPasswordPage) {
							navigate('/reset-password?username=' + res.data.email);
						}
						// minimal user object for unverified users
						const unverifiedUser = {
							data: {
								email: res.data.email,
								verified_at: null,
								...res.data,
							},
							role: [res.data.role],
							unverified: true,
							doNotRedirect: 1,
						};
						this.setSession(token, unverifiedUser);
						resolve(unverifiedUser);
						return;
					}

					// If user is verified, clear the needs_password_reset flag
					if (res.data.verified_at && needsPasswordReset) {
						secureLocalStorage.removeItem('needs_password_reset');
					}

					const user = {
						data: {
							email: res.data.email,
							displayName: `${res.data.first_name} ${res.data.last_name}`,
							photoURL: res.data.photo,
							...res.data,
							selected_company: company_id || res.data?.school.company_id || res.data?.school.id || null,
							selected_school: school_id || (res.data?.school.company_id ? res.data?.school.id : null) || null,
						},
						role: [res.data.role],
					};
					this.setSession(token, user);
					resolve(user);
				})
				.catch(() => {
					this.setSession(null);
					reject(new Error('Failed to get profile'));
				});
		});
	};

	signInWithSingleUseLogin = (token, hash, company_id = null, school_id = null) => {
		return new Promise((resolve, reject) => {
			axios
				.post('api/v2/login/token', { token, hash })
				.then((response) => {
					if (!response.data.access_token) {
						reject(response.data.message || 'Failed to login with token.');
						return;
					}

					this.signInWithToken(response.data.access_token, company_id, school_id, true)
						.then((user) => resolve(user))
						.catch((error) => reject(error));
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	updateUserData = (user) => {
		return axios.post('/api/auth/user/update', {
			user,
		});
	};

	setSession = (accessToken, user) => {
		if (accessToken) {
			secureLocalStorage.setItem('jwt_access_token', accessToken);
			if (user) {
				secureLocalStorage.setItem('user', JSON.stringify(user));
				this.setSchool(user.data?.selected_school || null);
				this.setCompany(user.data?.selected_company || null);
			}
			axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
		} else {
			localStorage.clear();
			this.setSchool(null);
			this.setCompany(null);
			delete axios.defaults.headers.common.Authorization;
		}
	};

	clearUser = () => {
		secureLocalStorage.removeItem('user');
		this.setSchool(null);
	};

	setSchool = (school_id) => {
		if (school_id) {
			secureLocalStorage.setItem('selected_school', school_id);
			axios.defaults.headers.common['X-School-Id'] = school_id;
		} else {
			secureLocalStorage.removeItem('selected_school');
			delete axios.defaults.headers.common['X-School-Id'];
		}
	};

	setCompany = (company_id) => {
		if (company_id) {
			secureLocalStorage.setItem('selected_company', company_id);
			axios.defaults.headers.common['X-Company-Id'] = company_id;
		} else {
			secureLocalStorage.removeItem('selected_company');
			delete axios.defaults.headers.common['X-Company-Id'];
		}
	};

	logout = async (redirect = true) => {
		try {
			this.setSession(null);
			this.setSchool(null);
			secureLocalStorage.clear();
			localStorage.clear();

			const token = this.getAccessToken();
			if (token) {
				try {
					await axios.post('api/v1/logout');
				} catch (err) {
					console.error('Logout API error:', err);
				}
			}

			if (redirect) {
				window.location.href = '/login';
			}
		} catch (error) {
			console.error('Logout error:', error);
			if (redirect) {
				window.location.href = '/login';
			}
		}
	};

	isAuthTokenValid = (accessToken) => {
		if (!accessToken) {
			return false;
		}

		return true;
	};

	getAccessToken = () => {
		return secureLocalStorage.getItem('jwt_access_token');
	};

	getProfile = (auth_token) => {
		return new Promise((resolve, reject) => {
			let headers = {};
			if (auth_token) {
				// If an auth token is provided, use it for the request
				headers.Authorization = `Bearer ${auth_token}`;
			}
			axios
				.get('/api/v2/profile', { headers: headers })
				.then((response) => {
					if (response.data) {
						// If user is unverified, store the needs_password_reset flag
						if (!response.data.verified_at) {
							secureLocalStorage.setItem('needs_password_reset', 'true');
						}
						resolve(response);
					} else {
						reject(new Error('Failed to get profile data.'));
					}
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	setViewAs = (otp, school_id) => {
		if (otp) {
			const tempUser = JSON.parse(secureLocalStorage.getItem('user'));
			tempUser.role[0] = 'super_school_admin';
			if (school_id) {
				tempUser.data.selected_school = school_id;
			}
			secureLocalStorage.setItem('user', JSON.stringify(tempUser));
		} else {
			delete axios.defaults.headers.common.Authorization;
		}
		this.setSchool(school_id);
	};
}

const instance = new JwtService();

export default instance;
